import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import menuData from '../config/menus.js';

const UIContext = createContext(undefined);
const UI_SETTINGS_KEY = 'sinamon-ui-settings';
const MOBILE_BREAKPOINT = 1024;
const SIDEBAR_WIDTH_MIN = 232;
const SIDEBAR_WIDTH_MAX = 420;
const SIDEBAR_WIDTH_DEFAULT = 240;

const defaultLayoutSettings = {
  autoCollapseSidebar: true,
  compactMode: true,
  sidebarCompact: false,
  autoHideNavbar: true,
  sidebarExpandedWidth: SIDEBAR_WIDTH_DEFAULT,
};

function clampSidebarWidth(width) {
  const numericWidth = Number(width);

  if (!Number.isFinite(numericWidth)) {
    return SIDEBAR_WIDTH_DEFAULT;
  }

  return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(numericWidth)));
}

function resolveInitialModule() {
  const modules = Object.keys(menuData);
  if (modules.includes('Cargas')) {
    return 'Cargas';
  }
  return modules[0] || '';
}

function resolveInitialCompactViewport() {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.innerWidth < MOBILE_BREAKPOINT;
}

function resolveShouldPinSidebar() {
  if (typeof window === 'undefined') {
    return true;
  }
  if (window.innerWidth < MOBILE_BREAKPOINT) {
    return false;
  }
  return !resolveInitialLayoutSettings().sidebarCompact;
}

function resolveInitialLayoutSettings() {
  if (typeof window === 'undefined') {
    return defaultLayoutSettings;
  }

  try {
    const raw = window.localStorage.getItem(UI_SETTINGS_KEY);
    if (!raw) {
      return defaultLayoutSettings;
    }
    return { ...defaultLayoutSettings, ...JSON.parse(raw) };
  } catch {
    return defaultLayoutSettings;
  }
}

export function UIProvider({ children }) {
  const [layoutSettings, setLayoutSettings] = useState(resolveInitialLayoutSettings);
  const [isCompactViewport, setIsCompactViewport] = useState(resolveInitialCompactViewport);
  const [isSidebarPinned, setIsSidebarPinned] = useState(resolveShouldPinSidebar);
  const [isSidebarHoverOpen, setIsSidebarHoverOpen] = useState(false);
  const [activeModule, setActiveModule] = useState(resolveInitialModule);
  // One value per module is intentional: the sidebar is an accordion, not a
  // collection of independent disclosure widgets.
  const [expandedSections, setExpandedSections] = useState(() => ({}));

  const updateLayoutSetting = useCallback((key, value) => {
    setLayoutSettings(prev => ({
      ...prev,
      [key]: key === 'sidebarExpandedWidth' ? clampSidebarWidth(value) : value,
    }));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncViewportMode = () => {
      const nextCompactViewport = window.innerWidth < MOBILE_BREAKPOINT;
      const nextShouldPinSidebar = resolveShouldPinSidebar();
      setIsCompactViewport(nextCompactViewport);
      setIsSidebarHoverOpen(false);

      setIsSidebarPinned(nextCompactViewport ? false : nextShouldPinSidebar);
    };

    syncViewportMode();
    window.addEventListener('resize', syncViewportMode);

    return () => {
      window.removeEventListener('resize', syncViewportMode);
    };
  }, [layoutSettings.sidebarCompact]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(UI_SETTINGS_KEY, JSON.stringify(layoutSettings));
  }, [layoutSettings]);

  const toggleSidebar = useCallback(() => {
    if (isCompactViewport) {
      setIsSidebarHoverOpen(prev => !prev);
      return;
    }
    setIsSidebarPinned(prev => {
      const next = !prev;
      updateLayoutSetting('sidebarCompact', !next);
      return next;
    });
  }, [isCompactViewport, updateLayoutSetting]);

  const openSidebarHover = useCallback(() => {
    setIsSidebarHoverOpen(true);
  }, []);

  const closeSidebarHover = useCallback(() => {
    setIsSidebarHoverOpen(false);
  }, []);

  const handleSetActiveModule = useCallback(module => {
    if (!menuData[module]) {
      return;
    }
    setActiveModule(module);
  }, []);

  const isSectionOpen = useCallback(
    (module, sectionLabel) => {
      return expandedSections[module] === sectionLabel;
    },
    [expandedSections],
  );

  const toggleSection = useCallback((module, sectionLabel) => {
    setExpandedSections(prev => {
      const current = prev[module];
      return { ...prev, [module]: current === sectionLabel ? null : sectionLabel };
    });
  }, []);

  const openSection = useCallback((module, sectionLabel) => {
    setExpandedSections(prev => (
      prev[module] === sectionLabel ? prev : { ...prev, [module]: sectionLabel }
    ));
  }, []);

  const isSidebarCollapsed = isCompactViewport ? !isSidebarHoverOpen : !isSidebarPinned;

  const value = useMemo(
    () => ({
      isCompactViewport,
      isSidebarPinned,
      isSidebarHoverOpen,
      isSidebarCollapsed,
      setIsSidebarPinned,
      toggleSidebar,
      openSidebarHover,
      closeSidebarHover,
      activeModule,
      setActiveModule: handleSetActiveModule,
      isSectionOpen,
      toggleSection,
      openSection,
      layoutSettings,
      updateLayoutSetting,
      sidebarExpandedWidth: clampSidebarWidth(layoutSettings.sidebarExpandedWidth),
    }),
    [
      activeModule,
      closeSidebarHover,
      handleSetActiveModule,
      isCompactViewport,
      isSectionOpen,
      openSection,
      isSidebarCollapsed,
      isSidebarHoverOpen,
      isSidebarPinned,
      layoutSettings,
      layoutSettings.sidebarExpandedWidth,
      openSidebarHover,
      toggleSection,
      toggleSidebar,
      updateLayoutSetting,
    ],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
}
